import { Typography } from '@material-ui/core'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import UpdateIcon from '@material-ui/icons/Update'
import React from 'react'
import { useBestPracticeTipCardStyles } from '../styles/useBestPracticeTipCardStyles'
import {
    BestPracticeTip,
    BPTipAction,
    BPTipParameters,
    parameterConfig,
} from '../types/bpManagement.types'

interface BestPracticeTipCardProps {
    tip: BestPracticeTip
}

const ACTION_LABELS: Record<BPTipAction, string> = {
    SET: 'BEST PRACTICE ASSIGNED',
    MODIFY: 'BEST PRACTICE MODIFIED',
    REMOVE: 'BEST PRACTICE REMOVED',
}

const ACTION_ICONS: Record<BPTipAction, React.ElementType> = {
    SET: CheckCircleOutlineIcon,
    MODIFY: UpdateIcon,
    REMOVE: HighlightOffIcon,
}

const formatTimestamp = (iso: string): string => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const today = new Date()
    const sameDay =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate()
    const timeText = d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    })
    if (sameDay) return `Today ${timeText}`
    const dateText = d.toLocaleDateString(undefined, {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    })
    return `${dateText} ${timeText}`
}

const computeInitials = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('')

const buildCriteriaPairs = (
    parameters: BPTipParameters | undefined
): { label: string; value: string }[] => {
    if (!parameters) return []
    return parameterConfig
        .map(({ key, label }) => ({
            label,
            value: parameters[key] ?? '',
        }))
        .filter((p) => p.value.length > 0)
}

export const BestPracticeTipCard = ({ tip }: BestPracticeTipCardProps) => {
    const classes = useBestPracticeTipCardStyles()
    const {
        action,
        type,
        author,
        occurredAt,
        parameters,
        changeDescription,
    } = tip

    const initials = computeInitials(author.name)
    const timestamp = formatTimestamp(occurredAt)
    const isParameter = type === 'parameter'
    const criteriaPairs = isParameter ? buildCriteriaPairs(parameters) : []
    const actionLabel = ACTION_LABELS[action]
    const ActionIcon = ACTION_ICONS[action]
    const showCriteria = isParameter && criteriaPairs.length > 0

    return (
        <div className={classes.card} data-testid="bestPracticeTipCard">
            <div className={classes.avatar} aria-hidden>
                {initials}
            </div>
            <div className={classes.body}>
                <div className={classes.headerRow}>
                    <Typography className={classes.userName}>
                        {author.name}
                    </Typography>
                    <Typography className={classes.timestamp}>
                        {timestamp}
                    </Typography>
                </div>
                <Typography className={classes.typeLabel}>
                    {showCriteria ? `${actionLabel}:` : actionLabel}
                </Typography>
                {showCriteria && (
                    <Typography className={classes.criteria}>
                        {criteriaPairs.map((p, idx) => (
                            <React.Fragment key={p.label}>
                                {idx > 0 && '   '}
                                <span className={classes.criteriaLabel}>
                                    {p.label}:
                                </span>{' '}
                                {p.value}
                            </React.Fragment>
                        ))}
                    </Typography>
                )}
                {action === 'MODIFY' && changeDescription && (
                    <Typography className={classes.criteria}>
                        {changeDescription}
                    </Typography>
                )}
            </div>
            <ActionIcon
                className={classes.actionIcon}
                aria-hidden
            />
        </div>
    )
}
